"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAINTENANCE_CHOICES = exports.yearPayments = exports.yearProduct = exports.YEAR_LETTERS = void 0;
exports.maintenanceYearDrafts = maintenanceYearDrafts;
exports.maintenanceOpportunityLines = maintenanceOpportunityLines;
// An accepted maintenance option as the opportunity's product lines (#515 slice 1b): one line per year
// per Jobpac cost code, year-lettered — year 1's are A01L / A11O / A21M / A31S / A41H / A51P, year 2's
// B…, up to L for year 12 — the products estimators key by hand today (PRICING.md "Maintenance contracts").
//   - labour by the repaint rules (decision 2, DD 23 Oct 2026), per area per year: a Repaint year's labour on
//     the option's labour type (Wages / Mixed → x01L, Subbie → x31S; foreman, abseil and allowance with it)
//     and its overtime on x11O at full cost; a Service year's on the servicing labour type; a Wash year's on
//     the option's;
//   - paint and consumables → x21M (litres), hire → x41H, other trades and the fuel levy → x51P;
//   - each line's sell is its cost grossed up at the contract's GP % (PAYG, decision 1), so a year's lines
//     add up to its Yearly Summaries column; every amount in whole dollars, as the repaint's lines;
//   - Even Spread and Higher Year One (DD, 2026-10-23): each year's lines share that year's payment, its finance
//     interest included, by their PAYG sell, and keep their cost: "the interest is not considered a cost. it
//     increases the project margin sent to jobpac".
// Pure: the API writes what this returns.
const accept_ts_1 = require("./accept.js");
/** CRM's year letters: A for year 1 … L for year 12. */
exports.YEAR_LETTERS = 'ABCDEFGHIJKL';
const SUBBIE = 'Subbie';
const HOURS_PLACES = 100;
const PERCENT = 100;
/** A cost code as year `year`'s product: the year letter in place of the code's first digit ("001L" → "B01L" for year 2). */
const yearProduct = (year, code) => `${exports.YEAR_LETTERS.charAt(year - 1)}${code.slice(1)}`;
exports.yearProduct = yearProduct;
const labourCode = (labourType) => (labourType === SUBBIE ? accept_ts_1.COST_CODES.subcontract : accept_ts_1.COST_CODES.normalTime);
/** An area's repaint as pieces (Take Off AA:AE): the same costs the Yearly Summaries' repaint column adds up. */
function repaintPieces(priced, rates, labourType) {
    const markupOther = 1 + rates.markupOther;
    const normalHours = priced.hours - priced.overtimeHours;
    const sum = (items, value) => items.reduce((t, x) => t + value(x), 0);
    return [
        {
            code: labourCode(labourType),
            cost: normalHours * rates.labour.cost +
                priced.hours * rates.allowance.cost +
                priced.foremanHours * rates.foreman.cost +
                priced.abseilHours * rates.abseil.cost,
            hoursLitres: normalHours,
        },
        { code: accept_ts_1.COST_CODES.overtime, cost: priced.overtimeHours * (rates.labour.cost + rates.overtime.cost), hoursLitres: priced.overtimeHours },
        {
            code: accept_ts_1.COST_CODES.paint,
            cost: sum(priced.surfaces, (s) => (s.litres * s.materialRate) / (1 + rates.markupMaterials)) +
                priced.sundryLitres * (rates.paints[0]?.costPerLitre ?? 0) +
                priced.consumables / markupOther,
            hoursLitres: priced.litres,
        },
        { code: accept_ts_1.COST_CODES.hire, cost: sum(priced.access, (a) => a.sell) / markupOther, hoursLitres: 0 },
        { code: accept_ts_1.COST_CODES.prelims, cost: priced.fuelLevy + sum(priced.otherTrades, (o) => o.sell) / markupOther, hoursLitres: 0 },
    ];
}
/** An area's year of servicing as pieces: its labour on the servicing labour type. */
function servicePieces(section, servicingLabourType) {
    const { cost, hours, litres } = section.service;
    return [
        { code: labourCode(servicingLabourType), cost: cost.labour, hoursLitres: hours },
        { code: accept_ts_1.COST_CODES.paint, cost: cost.materials, hoursLitres: litres },
        { code: accept_ts_1.COST_CODES.hire, cost: cost.hire, hoursLitres: 0 },
        { code: accept_ts_1.COST_CODES.prelims, cost: cost.prelims, hoursLitres: 0 },
    ];
}
function piecesFor(work, priced, section, rates, types) {
    if (work === 'repaint')
        return repaintPieces(priced, rates, types.labourType);
    if (work === 'service')
        return servicePieces(section, types.servicingLabourType);
    if (work === 'wash')
        return [{ code: labourCode(types.labourType), cost: section.wash.cost.labour, hoursLitres: section.wash.hours }];
    return [];
}
const LITRES_OR_HOURS = new Set([accept_ts_1.COST_CODES.normalTime, accept_ts_1.COST_CODES.subcontract, accept_ts_1.COST_CODES.overtime, accept_ts_1.COST_CODES.paint]);
/** A financed contract's year payments, interest included, for its year lines; none (undefined) for PAYG. */
const yearPayments = (yearly) => (yearly.paymentType === 'PAYG' ? undefined : yearly.years.map((y) => y.sell));
exports.yearPayments = yearPayments;
/**
 * Each year's lines rescaled to that year's payment (`payments`, interest included), by their sell; costs kept.
 * A year with a payment but no work selling anything (all areas No Work) puts the payment on its prelims line,
 * x51P, at no cost (DD, 2026-10-23: "put it on prelims").
 */
function toPayments(drafts, payments) {
    const sellOf = new Map();
    for (const d of drafts)
        sellOf.set(d.year, (sellOf.get(d.year) ?? 0) + d.sell);
    const out = [];
    payments.forEach((payment, i) => {
        const year = i + 1;
        const lines = drafts.filter((d) => d.year === year);
        const sell = sellOf.get(year) ?? 0;
        if (sell > 0 || payment <= 0) {
            out.push(...lines.map((d) => ({ ...d, sell: sell > 0 ? (d.sell * payment) / sell : 0 })));
            return;
        }
        const prelims = (0, exports.yearProduct)(year, accept_ts_1.COST_CODES.prelims);
        out.push(...lines.filter((d) => d.productNumber !== prelims));
        out.push({ year, productNumber: prelims, sell: payment, cost: lines.find((d) => d.productNumber === prelims)?.cost ?? 0, hoursLitres: null });
    });
    return out;
}
/**
 * Every year's lines, unrounded: what the Yearly Summaries' columns add up to, split by cost code. With
 * `payments` (a financed contract's year payments, interest included), each year's lines sell its payment.
 */
function maintenanceYearDrafts(option, contract, rates, types, payments) {
    const grossUp = contract.gp < 1 ? 1 / (1 - contract.gp) : 0;
    const drafts = [];
    for (let year = 1; year <= contract.years; year++) {
        const byCode = new Map();
        contract.sections.forEach((section, i) => {
            const priced = option.sections[i];
            if (!priced)
                return;
            for (const piece of piecesFor(section.programme[year - 1] ?? 'none', priced, section, rates, types)) {
                const into = byCode.get(piece.code) ?? { code: piece.code, cost: 0, hoursLitres: 0 };
                byCode.set(piece.code, { code: piece.code, cost: into.cost + piece.cost, hoursLitres: into.hoursLitres + piece.hoursLitres });
            }
        });
        // In COST_CODES order: labour, subbie labour, overtime, paint, hire, prelims.
        for (const code of Object.values(accept_ts_1.COST_CODES)) {
            const piece = byCode.get(code);
            if (!piece)
                continue;
            drafts.push({
                year,
                productNumber: (0, exports.yearProduct)(year, code),
                sell: piece.cost * grossUp,
                cost: piece.cost,
                hoursLitres: LITRES_OR_HOURS.has(code) ? Math.round(piece.hoursLitres * HOURS_PLACES) / HOURS_PLACES : null,
            });
        }
    }
    return payments ? toPayments(drafts, payments) : drafts;
}
const percentOf = (part, whole) => (whole > 0 ? Math.round((part / whole) * PERCENT * PERCENT) / PERCENT : 0);
/** The maintenance option's product lines, in whole dollars, years in order; lines with nothing on them are left out. */
function maintenanceOpportunityLines(option, contract, rates, types, payments) {
    return maintenanceYearDrafts(option, contract, rates, types, payments)
        .map((d) => ({ ...d, sell: Math.round(d.sell), cost: Math.round(d.cost) }))
        .filter((d) => d.sell !== 0 || d.cost !== 0)
        .map((d, i) => ({
        productNumber: d.productNumber,
        sequence: i + 1,
        sell: d.sell,
        cost: d.cost,
        hoursLitres: d.hoursLitres,
        marginDollar: d.sell - d.cost,
        marginPercent: percentOf(d.sell - d.cost, d.sell),
    }));
}
/** The opportunity's maintenance fields an accepted contract sets (decision 5: the schedule always matches the years). */
exports.MAINTENANCE_CHOICES = {
    /** hig_maintenancetype: PAYG …00, Even Spread …01, Higher Year One …02 (PAYMENT_TYPES' order). */
    typeBase: 910500000,
    /** hig_maintenanceschedule: N/A; 1 year = …01 … 12 years = …12, so the value is this + the term. */
    scheduleBase: 910500000,
    /** hig_maintenancetier: Enhance …00, Maintain …01, Protect …02 (the option's own column has the same values). */
    tierBase: 910500000,
};
